// Invoices — open, paid, or all; by client when the person has several.
// Each opens its public page: the invoice, Pay, and the PDF (docs/CLIENT_PORTAL.md §4).

import React, { useCallback, useEffect, useState } from 'react';
import { EmptyState, FwField, FwSelect, Notice, Pill, RecordList, Skeleton } from '../../../ui/fw';
import { get, money, day } from '../portalApi';
import { invoiceState, openInvoice } from '../portalShared';

export const FILTERS = [
  { key: 'open', label: 'Open' },
  { key: 'paid', label: 'Paid' },
  { key: 'all', label: 'All' },
];

export default function Invoices({ me, onUnauthorized }) {
  const billingClients = (me.clients || []).filter((c) => c.access === 'billing');
  const [status, setStatus] = useState('open');
  const [clientId, setClientId] = useState('');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    setRows(null);
    try {
      const q = new URLSearchParams({ status });
      if (clientId) q.set('client_id', clientId);
      const r = await get(`/portal/invoices?${q.toString()}`);
      setRows(r.invoices || []);
    } catch (e) {
      if (e.status === 401) onUnauthorized(); else setError(e.message);
    }
  }, [status, clientId, onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: 'invoice_number', label: 'Invoice', role: 'title' },
    ...(billingClients.length > 1 ? [{ key: 'client_name', label: 'For', role: 'subtitle' }] : []),
    { key: 'state', label: 'Status', role: 'badge', render: (r) => { const s = invoiceState(r); return <Pill tone={s.tone}>{s.label}</Pill>; } },
    { key: 'total', label: 'Total', align: 'right', render: (r) => money(r.total_cents) },
    // On Paid the balance is always $0.00, and a phone card has three fact
    // slots (RECORD_FACT_LIMIT): Total, Balance and Issued took them, and the
    // day it was paid — most of why that filter is opened — was the one
    // dropped (review of #724). Off the card only; the table keeps it.
    { key: 'balance', label: 'Balance', align: 'right', ...(status === 'paid' ? { role: 'hidden' } : {}), render: (r) => money(r.balance_cents) },
    { key: 'issued', label: 'Issued', render: (r) => day(r.issued_on) },
    { key: 'paid', label: status === 'open' ? 'Due' : 'Paid on', render: (r) => (status === 'open' ? day(r.due_on) : day(r.paid_on)) },
  ];

  return (
    <div className="pt-screen" data-screen="portal-invoices">
      <h1 className="pt-h1">Invoices</h1>
      <div className="pt-filters">
        <div className="pt-seg" role="group" aria-label="Show">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" className={`pt-seg__btn${status === f.key ? ' pt-seg__btn--on' : ''}`}
              aria-pressed={status === f.key} onClick={() => setStatus(f.key)} data-filter={f.key}>{f.label}</button>
          ))}
        </div>
        {billingClients.length > 1 ? (
          <FwField label="For" htmlFor="pt-inv-client">
            <FwSelect id="pt-inv-client" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Everyone</option>
              {billingClients.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </FwSelect>
          </FwField>
        ) : null}
      </div>
      {error ? <Notice tone="stop" title="Invoices could not be loaded" data-note="load-failed">{error}</Notice> : null}
      {!error && !rows ? <Skeleton lines={5} /> : null}
      {rows && rows.length ? <RecordList columns={columns} rows={rows} onRowClick={openInvoice} caption="Invoices" /> : null}
      {rows && !rows.length ? (
        <EmptyState icon="billing" title={status === 'open' ? 'Nothing owed' : 'No invoices here'}>
          {status === 'open' ? 'Every invoice is paid.' : 'Try another filter.'}
        </EmptyState>
      ) : null}
    </div>
  );
}
